import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MediGenieLogo } from "../components/MediGenieLogo";
import api, { apiErrorMessage } from "../lib/api";
import { toast } from "sonner";

export default function PatientRegister() {
  const navigate = useNavigate();
  const captchaRef = useRef(null);
  const widgetRef = useRef(null);
  const params = new URLSearchParams(window.location.search);
  const pharmacy = params.get("workspace") === "pharmacy";
  const loginUrl = "/login" + (pharmacy ? window.location.search : "");
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  const [resendAt, setResendAt] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [siteKey, setSiteKey] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [step, setStep] = useState("details");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", code: "" });

  useEffect(() => {
    api.get("/auth/patient-registration/config").then(({ data }) => { setConfig(data); setSiteKey(data.enabled ? data.site_key : ""); }).catch(() => setError("Registration settings could not be loaded. Please refresh and try again."));
  }, []);
  useEffect(() => {
    if (!siteKey || step !== "details") return;
    const render = () => {
      if (window.grecaptcha && captchaRef.current && widgetRef.current == null)
        widgetRef.current = window.grecaptcha.render(captchaRef.current, { sitekey: siteKey, callback: setCaptchaToken, "expired-callback": () => setCaptchaToken("") });
    };
    if (window.grecaptcha) render();
    else {
      const script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true; script.defer = true; script.onload = render;
      document.head.appendChild(script);
    }
  }, [siteKey, step]);

  useEffect(() => { const tick = () => setRemaining(Math.max(0, Math.ceil((resendAt - Date.now()) / 1000))); tick(); const timer = setInterval(tick, 1000); return () => clearInterval(timer); }, [resendAt]);

  const requestCode = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      await api.post("/auth/patient-registration/request-code", { ...form, captcha_token: captchaToken });
      setStep("verify"); setResendAt(Date.now() + 60000); setForm(v => ({ ...v, code: "" })); widgetRef.current = null; setCaptchaToken(""); toast.success("Verification code sent to your email");
    } catch (error) { setError(apiErrorMessage(error, "Registration could not be started")); }
    finally { setBusy(false); }
  };
  const verify = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      await api.post("/auth/patient-registration/verify", { email: form.email, code: form.code });
      toast.success("Email verified. Your account is ready to sign in."); navigate(loginUrl);
    } catch (error) { setError(apiErrorMessage(error, "Verification failed")); }
    finally { setBusy(false); }
  };
  const field = (key) => ({ value: form[key], onChange: (e) => setForm({ ...form, [key]: e.target.value }) });

  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
    <div className="w-full max-w-lg mg-card p-7">
      <Link to="/" className="flex justify-center mb-5"><MediGenieLogo size={58} /></Link>
      <h1 className="text-2xl font-extrabold text-mg-navy">{pharmacy ? "Create your Medigini account" : "Create your patient account"}</h1>
      <p className="text-sm text-slate-500 mt-1">Register online, verify your email, then sign in securely.</p>
      {error && <p role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {step === "details" ? <form onSubmit={requestCode} className="mt-6 space-y-4">
        <label className="block text-sm font-medium">Full name<input className="mg-input mt-1" required {...field("name")} /></label>
        <label className="block text-sm font-medium">Email address<input className="mg-input mt-1" type="email" autoComplete="email" required {...field("email")} /></label>
        <label className="block text-sm font-medium">Phone number <span className="text-slate-400">(optional)</span><input className="mg-input mt-1" {...field("phone")} /></label>
        <label className="block text-sm font-medium">Password<input className="mg-input mt-1" type="password" autoComplete="new-password" minLength="8" required {...field("password")} /></label>
        {siteKey && <div ref={captchaRef} />}
        {config?.email_available === false && <p role="status" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">Email registration is temporarily unavailable. Please try again once Medigini email delivery is configured.</p>}
        <button className="mg-btn-primary w-full justify-center" disabled={busy || !config || config.email_available === false || (siteKey && !captchaToken)}>{busy ? "Sending…" : "Send verification code"}</button>
      </form> : <form onSubmit={verify} className="mt-6 space-y-4">
        <p className="rounded-lg bg-blue-50 p-3 text-sm text-mg-navy">Enter the six-digit code sent to <strong>{form.email}</strong>. It expires in 10 minutes.</p>
        <label className="block text-sm font-medium">Verification code<input className="mg-input mt-1 text-center text-xl tracking-[0.35em]" autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" maxLength="6" required {...field("code")} /></label>
        <button className="mg-btn-primary w-full justify-center" disabled={busy}>{busy ? "Verifying…" : "Verify and create account"}</button>
      <button type="button" className="text-sm font-semibold text-mg-medical" disabled={busy || remaining > 0} onClick={() => { setStep("details"); setError(""); }}>Edit details or resend{remaining > 0 ? ` (${remaining}s)` : ""}</button>
      </form>}
      <div className="mt-6 text-center text-sm text-slate-500">Already registered? <Link className="font-semibold text-mg-medical" to={loginUrl}>Sign in</Link></div>
    </div>
  </div>;
}
